/**
 * Mutation hook for exporting home visit patient schedule as CSV (CSV出力).
 *
 * Downloads CSV file via blob. Uses hitowa-common toast messages.
 */

import { useMutation } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants/messages';
import { exportHomeVisitPatientsCsv } from '@/services/main/homeVisitPatient';
import type { ExportHomeVisitPatientCsvPayload } from '@/pages/main/homeVisitPatient/types';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';

function getServiceTypeFallback(): string {
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

export function useExportHomeVisitPatientCsvMutation() {
  const { tenant, clinicNumber } = useClinicContext();
  const theme = useTheme();
  const serviceType =
    theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
      ? theme.serviceType
      : getServiceTypeFallback();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    ExportHomeVisitPatientCsvPayload
  >({
    mutationFn: payload => exportHomeVisitPatientsCsv(tenant, clinicNumber, payload, serviceType),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: () => {
      toast.error(CSV_ERROR_MESSAGE);
    },
  });

  const triggerExport = async (payload: ExportHomeVisitPatientCsvPayload) => {
    try {
      const blob = await mutateAsync(payload);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '予定表（患者毎）.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      // onError already handles toast
    }
  };

  return { triggerExport, isLoading };
}
